import React from 'react'
import { Campaign, Series } from '../../types'
import { TileGrid } from '../../ui/Tile'
import TileGraph from '../../ui/TileGraph'
import { useTranslation } from 'react-i18next'

export interface CampaignMetrics {
    sent: Series[]
    deliveryRate: Series[]
    opened: Series[]
    clicked: Series[]
    unsubscribed: Series[]
}

export function parseSeries(series: Series[]): Series[] {
    return series.map(s => ({
        ...s,
        data: s.data.map(m => ({
            ...m,
            date: typeof m.date === 'string' ? Date.parse(m.date) : m.date,
        })),
    }))
}

interface CampaignStatsProps extends Pick<Campaign, 'state' | 'delivery' | 'progress'> {
    broadcast?: boolean
    metrics?: CampaignMetrics
    dateFormat?: string
    onExpandMetric?: (title: string, key: keyof CampaignMetrics) => void
}

export default function CampaignStats({ state, delivery, progress, broadcast, metrics, dateFormat, onExpandMetric }: CampaignStatsProps) {
    const { t } = useTranslation()
    const percent = new Intl.NumberFormat(undefined, { style: 'percent', minimumFractionDigits: 2 })

    const sent = delivery.sent.toLocaleString()
    const total = progress
        ? progress.total.toLocaleString()
        : delivery.total.toLocaleString()
    const deliveryRate = percent.format(delivery.total ? delivery.sent / delivery.total : 0)
    const openRate = percent.format(delivery.total ? delivery.opens / delivery.total : 0)
    const clickRate = percent.format(delivery.total ? delivery.clicks / delivery.total : 0)
    const unsubscribedRate = percent.format(delivery.total ? delivery.unsubscribes / delivery.total : 0)
    const tiles = delivery.unsubscribes != null ? 5 : 4

    const SentSpan: React.ReactNode = <span>{sent}/<small>{state === 'loading' ? `~${total}` : total}</small></span>

    return (
        <TileGrid numColumns={tiles}>
            {broadcast
                ? <>
                    <TileGraph title={total}>
                        {t('audience')}
                    </TileGraph>
                    <TileGraph
                        title={sent}
                        series={metrics?.sent}
                        dateFormat={dateFormat}
                        onClick={metrics?.sent ? () => onExpandMetric?.(t('delivered'), 'sent') : undefined}
                    >
                        {t('delivered')}
                    </TileGraph>
                </>
                : <>
                    <TileGraph
                        title={SentSpan}
                        series={metrics?.sent}
                        dateFormat={dateFormat}
                        onClick={metrics?.sent ? () => onExpandMetric?.(t('sent'), 'sent') : undefined}
                    >
                        {t('sent')}
                    </TileGraph>
                    <TileGraph
                        title={deliveryRate}
                        series={metrics?.deliveryRate}
                        dateFormat={dateFormat}
                        formatter={v => `${v}%`}
                        onClick={metrics?.deliveryRate ? () => onExpandMetric?.(t('delivery_rate'), 'deliveryRate') : undefined}
                    >
                        {t('delivery_rate')}
                    </TileGraph>
                </>
            }
            <TileGraph
                title={openRate}
                series={metrics?.opened}
                dateFormat={dateFormat}
                onClick={metrics?.opened ? () => onExpandMetric?.(t('open_rate'), 'opened') : undefined}
            >
                {t('open_rate')}
            </TileGraph>
            <TileGraph
                title={clickRate}
                series={metrics?.clicked}
                dateFormat={dateFormat}
                onClick={metrics?.clicked ? () => onExpandMetric?.(t('click_rate'), 'clicked') : undefined}
            >
                {t('click_rate')}
            </TileGraph>
            {delivery.unsubscribes != null && (
                <TileGraph
                    title={unsubscribedRate}
                    series={metrics?.unsubscribed}
                    dateFormat={dateFormat}
                    onClick={metrics?.unsubscribed ? () => onExpandMetric?.(t('unsubscribe_rate'), 'unsubscribed') : undefined}
                >
                    {t('unsubscribe_rate')}
                </TileGraph>
            )}
        </TileGrid>
    )
}
